import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import useAnimalMeasurementContext from "../../hooks/useAnimalMeasurementContext";

import { CardCustom, Title } from "./styles";
import { Col, Icon, Row, Switch } from "antd";
import ButtonStandard from "../../components/utils/button";
import ExcelExportButton from "../../components/utils/excelExportButton";
import TableAnimalMeasurement from "./table";
import { indexAll } from "../../services/measurementService";

const AnimalMeasurementLayout = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const {
    tableProperties: { size, page, search },
    fetchData,
    initializeData,
    openOrCloseForm,
    shouldShowOnlyForActiveAnimals,
    setShouldShowOnlyForActiveAnimals,
  } = useAnimalMeasurementContext();

  const [measurementOptions, setMeasurementOptions] = useState([]);

  const fetchMeasurementOptions = useCallback(async () => {
    if (!groupId || !farmId) return;
    try {
      const {
        data: { results },
      } = await indexAll({ groupId, farmId });
      setMeasurementOptions(
        (results || [])
          .filter((m) => m.status !== "Inactive")
          .map((m) => ({ id: m.name, value: m.name }))
      );
    } catch (error) {
      setMeasurementOptions([]);
    }
  }, [groupId, farmId]);

  useEffect(() => {
    fetchMeasurementOptions();
  }, [fetchMeasurementOptions]);

  const handleRefresh = () => {
    fetchData(page, size, {}, search);
  };

  const handleCreateNewRecord = () => {
    initializeData(null);
    openOrCloseForm();
  };

  return (
    <CardCustom>
      <Row className="rowHeader" justify="space-between">
        <Col span={8}>
          <Title>{translation.animalMeasurement.title}</Title>
        </Col>
        <Col span={8}>
          <strong>Buscar medições somente de animais Ativos </strong>
          <Switch
            checked={shouldShowOnlyForActiveAnimals}
            onChange={(checked) => setShouldShowOnlyForActiveAnimals(checked)}
          ></Switch>
        </Col>
        <Col span={8}>
          <Row type="flex" justify="end" gutter={8}>
            <Col>
              <ExcelExportButton
                reportName="AnimalMeasurement"
                fileName={`Listagem_Medicoes_${moment().format("DDMMYYYY")}`}
                columns={{
                  status: translation.animalMeasurement.export.columns.status,
                  handlingNumber:
                    translation.animalMeasurement.export.columns.handlingNumber,
                  tagId: translation.animalMeasurement.export.columns.tagId,
                  gender: translation.animalMeasurement.export.columns.gender,
                  breedName:
                    translation.animalMeasurement.export.columns.breedName,
                  lastWeightDate:
                    translation.animalMeasurement.export.columns.lastWeightDate,
                  lastWeight:
                    translation.animalMeasurement.export.columns.lastWeight,
                  measurementDate:
                    translation.animalMeasurement.export.columns
                      .measurementDate,
                  measurementName:
                    translation.animalMeasurement.export.columns
                      .measurementName,
                  value: translation.animalMeasurement.export.columns.value,
                  tempPerm:
                    translation.animalMeasurement.export.columns.tempPerm,
                  exitDate:
                    translation.animalMeasurement.export.columns.exitDate,
                  exitWeight:
                    translation.animalMeasurement.export.columns.exitWeight,
                }}
                filters={[
                  {
                    field: "status_f",
                    label: translation.animalMeasurement.export.filters.status,
                    type: "Options",
                    operator: "IN",
                    options: [
                      {
                        id: "A",
                        value:
                          translation.animalMeasurement.export.statusOptions
                            .active,
                      },
                      {
                        id: "S",
                        value:
                          translation.animalMeasurement.export.statusOptions
                            .sold,
                      },
                      {
                        id: "D",
                        value:
                          translation.animalMeasurement.export.statusOptions
                            .dead,
                      },
                    ],
                  },
                  {
                    field: "gender_f",
                    label: translation.animalMeasurement.export.filters.gender,
                    type: "Options",
                    operator: "IN",
                    options: [
                      {
                        id: "M",
                        value:
                          translation.animalMeasurement.export.genderOptions
                            .male,
                      },
                      {
                        id: "F",
                        value:
                          translation.animalMeasurement.export.genderOptions
                            .female,
                      },
                    ],
                  },
                  {
                    field: "breedName",
                    label: translation.animalMeasurement.export.filters.breed,
                    type: "String",
                    operator: "LIKE",
                  },
                  {
                    field: "measurementDate",
                    label:
                      translation.animalMeasurement.export.filters
                        .measurementDate,
                    type: "date",
                    operator: "BETWEEN",
                  },
                  {
                    field: "measurementName",
                    label:
                      translation.animalMeasurement.export.filters.measurement,
                    type: "Options",
                    operator: "IN",
                    options: measurementOptions,
                  },
                  {
                    field: "value_f",
                    label: translation.animalMeasurement.export.filters.value,
                    type: "Number",
                    operator: "GTE",
                  },
                  {
                    field: "lotName",
                    label: translation.animalMeasurement.export.filters.lot,
                    type: "String",
                    operator: "LIKE",
                  },
                  {
                    field: "picketName",
                    label: translation.animalMeasurement.export.filters.picket,
                    type: "String",
                    operator: "LIKE",
                  },
                ]}
              />
            </Col>
            <Col>
              <ButtonStandard
                buttonType="typeWithoutBackground"
                onClick={handleRefresh}
              >
                <Icon type="reload" />
              </ButtonStandard>
            </Col>
            <Col>
              <ButtonStandard
                name="buttonCreateMeasurement"
                buttonType="addNew"
                onClick={handleCreateNewRecord}
              >
                {translation.animalMeasurement.buttonNew}
              </ButtonStandard>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row type="flex">
        <Col span={24}>
          <TableAnimalMeasurement />
        </Col>
      </Row>
    </CardCustom>
  );
};

export default AnimalMeasurementLayout;
